import React, { useEffect, useState } from 'react'
import { adminRequest } from '../api/client'
import { Card, DefinitionList, EmptyState, PageTitle, ProgressBar, StatusBadge, formatCurrency, formatDate } from '../components/Ui'

function budgetLabel(request) {
  if (request?.budgetText) return request.budgetText
  if (request?.budgetMin != null || request?.budgetMax != null) return `${formatCurrency(request.budgetMin)} ~ ${formatCurrency(request.budgetMax)}`
  return '협의'
}

export default function QuoteRequestDetail({ id, navigate }) {
  const [request, setRequest] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    setRequest(null)
    setError('')
    adminRequest(`/api/v1/admin/quote-requests/${encodeURIComponent(id)}`)
      .then((response) => { if (!disposed) setRequest(response?.data || null) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || '견적 요청을 찾을 수 없습니다.') })
    return () => { disposed = true }
  }, [id])

  if (error) return <><div className="breadcrumb"><button onClick={() => navigate('/quote-requests')}>견적 요청</button><span>›</span><span>{id}</span></div><Card><EmptyState title="견적 요청 조회 실패" description={error} /></Card></>
  if (!request) return <><PageTitle eyebrow="QUOTE REQUEST DETAIL · LIVE DB" title="견적 요청 확인 중" description="difan_dev에서 요청 원문과 연결 데이터를 불러오고 있습니다." /><Card><p>실제 견적 요청을 조회하는 중입니다.</p></Card></>

  const attachments = request.attachments || []
  const summary = request.aiSummary || null
  const targets = request.targets || []
  const quotes = request.quotes || []

  return (
    <>
      <div className="breadcrumb"><button onClick={() => navigate('/quote-requests')}>견적 요청</button><span>›</span><span>{request.requestNo}</span></div>
      <PageTitle eyebrow="QUOTE REQUEST DETAIL · LIVE DB" title={request.serviceName || '견적 요청'} description={`${request.requestNo} · ${request.customerNickname || request.customerName || '-'}`} actions={<StatusBadge status={request.status} />} />
      <div className="detail-summary-bar">
        <div><span>서비스</span><strong>{request.serviceName || '-'}</strong></div>
        <div><span>행사일</span><strong>{request.eventDate || '협의'}</strong></div>
        <div><span>지역</span><strong>{request.region || '협의'}</strong></div>
        <div><span>예산</span><strong>{budgetLabel(request)}</strong></div>
      </div>

      <div className="detail-grid quote-detail-grid">
        <div className="detail-main-column">
          <Card title="고객 요청 원문" description="회원이 실제 저장한 quote_requests.original_request입니다.">
            <blockquote className="request-original">{request.originalRequest || '-'}</blockquote>
            <div className="mock-attachments"><span>참고 이미지·자료</span><small>PRIVATE Object Storage · {attachments.length}개</small></div>
            {attachments.length ? <div className="admin-file-list">{attachments.map((file, index) => <a key={file.mediaId} href={file.url} target="_blank" rel="noreferrer"><span>▧</span><strong>첨부 이미지 {index + 1}</strong><small>{file.mediaId}</small></a>)}</div> : <p className="help-copy">첨부 이미지가 없습니다.</p>}
          </Card>

          <Card title="AI 구조화 분석" description="quote_request_ai_analyses에 저장된 실제 분석 결과입니다.">
            {summary ? <div className="structured-grid">
              <div><span>목적</span><strong>{summary.purpose || request.serviceName || '-'}</strong></div>
              <div><span>지역</span><strong>{summary.region || request.region || '협의'}</strong></div>
              <div className="span-2"><span>요청 요약</span><p>{summary.requestSummary || request.originalRequest}</p></div>
              <div><span>예상 방문 인원</span><strong>{summary.attendees || request.attendees || '협의'}</strong></div>
              <div><span>예산</span><strong>{summary.budgetText || budgetLabel(request)}</strong></div>
              <div className="span-2"><span>분석 메모</span><p>{summary.note || '-'}</p></div>
              <div className="span-2"><span>분석 신뢰도</span><ProgressBar value={Number(request.aiConfidence || 0) * 100} /></div>
            </div> : <EmptyState title="연결된 AI 분석이 없습니다." description="AI 분석 없이 직접 등록된 요청일 수 있습니다." />}
          </Card>

          <Card title="전달 디렉터" description="quote_request_targets에 실제 배정된 디렉터입니다.">
            {targets.length ? <div className="curation-list">{targets.map((item, index) => <article key={item.directorId}><b>{index + 1}</b><div className="curation-avatar">{(item.brandName || 'D').slice(0, 1)}</div><div className="curation-copy"><strong>{item.brandName}</strong><p>전달 {formatDate(item.sentAt, true)} · {item.viewedAt ? `열람 ${formatDate(item.viewedAt, true)}` : '미열람'}</p><small>{item.targetStatus}</small></div><button className="button soft" onClick={() => navigate(`/directors/${item.directorId}`)}>디렉터 보기</button></article>)}</div> : <EmptyState title="전달된 디렉터가 없습니다." description="현재 이 요청에 배정된 디렉터가 없습니다." />}
          </Card>

          <Card title="도착 견적" description="해당 요청과 연결된 실제 quotes 상태입니다.">
            {quotes.length ? <div className="compact-table-wrap"><table className="data-table compact"><thead><tr><th>견적번호</th><th>디렉터</th><th>상태</th><th>수정일</th></tr></thead><tbody>{quotes.map((item) => <tr className="clickable-row" key={item.quoteId} onClick={() => navigate(`/quotes/${item.quoteId}`)}><td><strong>{item.quoteNo}</strong></td><td>{item.brandName}</td><td><StatusBadge status={item.status} /></td><td>{formatDate(item.updatedAt, true)}</td></tr>)}</tbody></table></div> : <EmptyState title="도착한 견적이 없습니다." description="디렉터가 견적을 제출하면 이곳에 표시됩니다." />}
          </Card>
        </div>

        <div className="detail-side-column">
          <Card title="요청 정보"><DefinitionList items={[
            { label: '요청 ID', value: request.id },
            { label: '고객 ID', value: request.customerId },
            { label: '고객', value: `${request.customerName || '-'} (${request.customerNickname || '-'})` },
            { label: '서비스 ID', value: request.serviceId || '-' },
            { label: '서비스 Slug', value: request.serviceSlug || '-' },
            { label: '서비스명', value: request.serviceName || '-' },
            { label: '예상 방문 인원', value: request.attendees || '-' },
            { label: '첨부 이미지', value: `${attachments.length}개` },
            { label: '요청일', value: formatDate(request.createdAt, true) },
            { label: '전달 디렉터', value: `${request.distributedDirectorCount || 0}명` },
            { label: '도착 견적', value: `${request.quoteCount || 0}건` },
          ]} /></Card>
          {summary ? <Card title="AI 실행 정보"><DefinitionList items={[
            { label: '분석 ID', value: request.aiAnalysisId || '-' },
            { label: '엔진', value: request.aiModelVersion || summary.engine || '-' },
            { label: 'Prompt', value: request.aiPromptVersion || '-' },
            { label: '분석일', value: formatDate(request.aiCreatedAt, true) },
          ]} /></Card> : null}
          <Card title="데이터 원본"><p className="help-copy">이 상세 화면에는 임의 예시 fallback을 사용하지 않습니다. 존재하지 않는 UUID는 실제 404로 처리됩니다.</p></Card>
        </div>
      </div>
    </>
  )
}
